import React from 'react'
import { useSelector } from 'react-redux';
import { PortInfo, setObjectType } from '../../utils';
import { validateConnection } from '../../utils/DesignerUtils';
import { Connection, Handle, NodeProps, Position } from 'reactflow';
import { EventEmitter } from '../../utils/EventEmitter';
import { getUsedItemSelector } from '../../slices/designerSlice';
import { UsedItem } from '../../models/generics';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaService } from '../../models/BdaService';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';

type McpServerNodeData = {
  inputs: PortInfo[];
  outputs: PortInfo[];
  text: string;
  label: string;
  llmModel: string;
  prompt: string;
}

export default function McpServerNode({ id, data, isConnectable }: NodeProps<McpServerNodeData>) {
  const getUsedItem = useSelector(getUsedItemSelector);

  const isValidConnection = (connection: Connection): boolean => {
    const isValid = connection.source !== connection.target && validateConnection(connection.sourceHandle!, connection.targetHandle!);
    if (!isValid) return false;

    const sourceItem = getUsedItem(connection.source)!;

    if (sourceItem.type === 'dataset' && setObjectType<UsedItem<BdaDataset>>(sourceItem)) { // targetItem can only be the current service
      const input = data.inputs.find(input => input.id === connection.targetHandle)!;
      return (sourceItem.datasource!.type === 'messageBroker' && input.datasetMode === 'streaming') || (sourceItem.datasource!.type !== 'messageBroker' && input.datasetMode !== 'streaming');
    }
    else if ((sourceItem.type === 'service') && setObjectType<UsedItem<BdaService>>(sourceItem)) { // TODO replace service type with just 'service' ???
      const targetItem = getUsedItem(connection.target) as UsedItem<BdaService>;
      const sourceOutput = (sourceItem.outputs as PortInfo[]).find(output => output.id === connection.sourceHandle);
      const targetInput = (targetItem.inputs as PortInfo[]).find(input => input.id === connection.targetHandle);
      return sourceOutput?.datasetMode === targetInput?.datasetMode;
    }

    return true;
  }
  const handlePortClick = (portId: string, generic?: boolean, description?: string) => (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.stopPropagation();

    const portInfo: PortInfo = {
      id: portId,
      nodeId: id,
      nodeName: data.text,
      description,
      generic,
    };
    EventEmitter.dispatch('selected-port', portInfo);
  }

  return (
    <div className="designer-node mcp-node">
      <div className="input-ports">
        {data.inputs.map(({ id, type, datasetMode, generic, description }) => (
          <Handle
            className={classNames("custom-handle service-handle", type, datasetMode, { generic })}
            key={id}
            type="target"
            position={Position.Top}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id, generic, description)}
          />
        ))}
      </div>

      <h6 className="m-0">
        <FontAwesomeIcon icon={solid('hand-sparkles')} />
        <span className="ms-2">{data.label || data.text}</span>
      </h6>

      <div className="output-ports">
        {data.outputs.map(({ id, type, datasetMode, generic, description }) => (
          <Handle
            type="source"
            key={id}
            className={classNames("custom-handle service-handle", type, datasetMode, { generic })}
            position={Position.Bottom}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id, generic, description)}
          />
        ))}
      </div>
    </div>
  );
}
